import { roundToPrecision } from '@cf-shared/shared-util';

/**
 * Calculate trailing average for a data series.
 * Used by cs-progress-chart when showAvg is enabled.
 *
 * @param data - Array of values (with undefined for missing data points)
 * @param precision - Number of decimal places for rounding
 * @param windowSize - Number of days to include in the average (default: 7)
 * @returns Array of averaged values
 */
export function calculateTrailingAverage(
  data: (number | undefined)[],
  precision: number,
  windowSize: number = 7
): (number | undefined)[] {
  // calculate trailing average (current day + previous days within window)
  return data.map((_, index) => {
    const startIdx = Math.max(0, index - windowSize + 1);
    const window = data.slice(startIdx, index + 1).filter(v => v !== undefined) as number[];
    if (window.length > 0) {
      const average = window.reduce((a, b) => a + b, 0) / window.length;
      return roundToPrecision(average, precision);
    }
    return undefined;
  });
}

/**
 * Calculate min/max range for a data series with padding.
 * Used by cs-progress-chart to set Y-axis bounds.
 *
 * @param data - Array of values (with undefined for missing data points)
 * @param paddingPercent - Percentage of range to add as padding (default: 0.1 = 10%)
 * @returns Object with min and max values
 */
export function calculateMinMax(
  data: (number | undefined)[],
  paddingPercent: number = 0.1
): { min: number; max: number } {
  const validValues = data.filter(v => v !== undefined) as number[];

  if (validValues.length === 0) {
    return { min: 0, max: 100 };
  }

  const min = Math.min(...validValues);
  const max = Math.max(...validValues);
  const range = max - min;

  // when the series is flat (all values equal — common on plateaus
  // and on stagnant exercise weight) the proportional padding rule
  // would collapse the Y axis to min === max, which Chart.js renders
  // as a degenerate scale with no ticks and an invisible line.
  // expand around the value instead: 10% of |value| with a small
  // absolute floor so an all-zero series also gets a visible band.
  if (range === 0) {
    const flatPadding = Math.max(Math.abs(min) * paddingPercent, 1);
    return {
      min: Math.max(0, min - flatPadding),
      max: max + flatPadding,
    };
  }

  const padding = range * paddingPercent;
  return {
    min: Math.max(0, min - padding),
    max: max + padding
  };
}

/**
 * Decide whether a chart series should render point markers.
 *
 * A Chart.js line dataset cannot draw a line for a single non-undefined
 * data point — and `cs-progress-chart` sets `pointRadius: 0` unless the
 * consumer opts in via `[showPoints]="true"`. So for a series with
 * exactly one valid point the lone value is invisible: no line, no
 * marker (#3704 — Bug B). Force a marker for that case so the value
 * still shows up.
 *
 * Multi-point series keep the existing "no markers unless asked"
 * behaviour to avoid cluttering dense lines.
 */
export function shouldShowMarker(
  values: (number | undefined)[],
  userShowPoints: boolean,
): boolean {
  if (userShowPoints) return true;
  let count = 0;
  for (const v of values) {
    if (v !== undefined) {
      count++;
      if (count > 1) return false;
    }
  }
  return count === 1;
}
